/**
 * Faucet cooldown error introspection.
 *
 * The backend rejects a too-soon claim with the `FAUCET_COOLDOWN` API code and
 * a `details.retryAfterSeconds` field carrying the time REMAINING on the
 * per-wallet cooldown. The v2 SDK preserves `apiCode`, `details`, and (for 429s)
 * `retryAfterMs` as own properties on the thrown `SdkError`, so these reads work
 * against the error the mutation `onError` receives. Pure + framework-free so it
 * can be unit-tested in isolation.
 */

const FAUCET_COOLDOWN_CODE = 'FAUCET_COOLDOWN';

interface SdkErrorLike {
  // ALLOW-UNKNOWN: SDK error properties are dynamically shaped at runtime; typed after narrowing below
  readonly apiCode?: unknown;
  // ALLOW-UNKNOWN: SDK error properties are dynamically shaped at runtime; typed after narrowing below
  readonly details?: unknown;
  // ALLOW-UNKNOWN: SDK error properties are dynamically shaped at runtime; typed after narrowing below
  readonly retryAfterMs?: unknown;
}

// ALLOW-UNKNOWN: error originates from an untyped SDK catch boundary; narrowed structurally in the function body
function asSdkError(error: unknown): SdkErrorLike | null {
  if (typeof error !== 'object' || error === null) return null;
  return error as SdkErrorLike;
}

/** True when the error is the backend's per-wallet faucet cooldown rejection. */
// ALLOW-UNKNOWN: error originates from an untyped SDK catch boundary; narrowed by asSdkError below
export function isFaucetCooldownError(error: unknown): boolean {
  return asSdkError(error)?.apiCode === FAUCET_COOLDOWN_CODE;
}

/**
 * Seconds remaining before the wallet may claim again, or `null` when the error
 * carries no usable timing. Prefers the backend's `details.retryAfterSeconds`
 * and falls back to the `Retry-After`-derived `retryAfterMs`.
 */
// ALLOW-UNKNOWN: error originates from an untyped SDK catch boundary; narrowed by asSdkError below
export function extractFaucetCooldownSeconds(error: unknown): number | null {
  const e = asSdkError(error);
  if (e === null) return null;

  const details = e.details;
  if (typeof details === 'object' && details !== null && 'retryAfterSeconds' in details) {
    // ALLOW-UNKNOWN: details shape is runtime-determined; retryAfterSeconds is narrowed by typeof check below
    const raw = (details as { retryAfterSeconds: unknown }).retryAfterSeconds;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      return Math.ceil(raw);
    }
  }

  if (typeof e.retryAfterMs === 'number' && Number.isFinite(e.retryAfterMs) && e.retryAfterMs > 0) {
    return Math.ceil(e.retryAfterMs / 1000);
  }

  return null;
}

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

/** Coarse "come back in X" unit, decoupled from i18n so every faucet surface
 *  (toast hook + modal) picks the same plural message key from the same math. */
export type CooldownDisplay =
  | { unit: 'hours'; value: number }
  | { unit: 'minutes'; value: number }
  | { unit: 'soon' };

export function cooldownDisplay(seconds: number | null): CooldownDisplay {
  if (seconds !== null && seconds >= SECONDS_PER_HOUR) {
    return { unit: 'hours', value: Math.ceil(seconds / SECONDS_PER_HOUR) };
  }
  if (seconds !== null && seconds >= SECONDS_PER_MINUTE) {
    return { unit: 'minutes', value: Math.ceil(seconds / SECONDS_PER_MINUTE) };
  }
  return { unit: 'soon' };
}
